import React from "react";

const defaultFormatChars: { [x: string]: string } = {
    "9": "[0-9]",
    a: "[A-Za-z]",
    "*": "[A-Za-z0-9]",
};

export type MaskOptionsType = {
    mask: string;
    formatChars: { [x: string]: string };
    lastEditablePosition: number;
    permanents: Array<number>;
};

function parseMask(mask: string): MaskOptionsType {
    let parsedMaskString: string = "";
    let lastEditablePosition: number = 0;

    const permanents: Array<number> = [];

    const formatChars = defaultFormatChars;

    let isPermanent = false;

    mask.split("").forEach((character) => {
        if (!isPermanent && character === "\\") {
            isPermanent = true;
        } else {
            if (isPermanent || !formatChars[character]) {
                permanents.push(parsedMaskString.length);
            } else {
                lastEditablePosition = parsedMaskString.length + 1;
            }

            parsedMaskString += character;
            isPermanent = false;
        }
    });

    return {
        mask: parsedMaskString,
        formatChars,
        lastEditablePosition,
        permanents,
    };
}

function isPermanentCharacter(maskOptions: MaskOptionsType, pos: number): boolean {
    return maskOptions.permanents.indexOf(pos) !== -1;
}

function isAllowedCharacter(maskOptions: MaskOptionsType, pos: number, character: string): boolean {
    if (!character) {
        return false;
    }

    if (isPermanentCharacter(maskOptions, pos)) {
        return maskOptions.mask[pos] === character;
    }

    const ruleChar = maskOptions.mask[pos];
    const charRule = maskOptions.formatChars[ruleChar];

    return new RegExp(charRule).test(character);
}

export type InputMaskHookParams = {
    setValue: (value: React.SetStateAction<string>) => void;
    mask: string;
};

export type InputMaskHook = {
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

function useInputMask({ setValue, mask }: InputMaskHookParams): InputMaskHook {
    const maskOptions = React.useMemo(() => parseMask(mask), [mask]);

    const onChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setValue((prevValue: string) => {
                let newValue: string = event.target.value + "";

                const everyValue = newValue.split("").every((character, i) => {
                    return isAllowedCharacter(maskOptions, i, character);
                });

                if (everyValue) {
                    if (maskOptions.permanents.indexOf(newValue.length) !== -1) {
                        if (newValue.length > prevValue.length) {
                            newValue += maskOptions.mask[newValue.length];
                        } else {
                            newValue = newValue.substring(0, newValue.length - 1);
                        }
                    }

                    if (newValue.length <= maskOptions.lastEditablePosition) {
                        return newValue;
                    } else {
                        return prevValue;
                    }
                } else {
                    return prevValue;
                }
            });
        },
        [setValue, maskOptions]
    );

    return React.useMemo(
        () => ({
            onChange,
        }),
        [onChange]
    );
}

export default useInputMask;
